// NDLCS Skills Courses — short, free, skills-based courses authored in-house.
// These are NOT accredited and do NOT carry NQF / SAQA recognition.
// Completion earns an NDLCS professional-development certificate only.

export type NdlcsSkillsCourse = {
  slug: string;
  code: string;
  title: string;
  category:
    | "Workplace & Labour"
    | "POPIA & Compliance"
    | "Personal Finance & Debt"
    | "Contracts & Legal Literacy";
  shortDescription: string;
  outcomes: string[];
  estimatedHours: number;
  level: "Beginner" | "Intermediate";
};

export const NDLCS_SKILLS_COURSES: NdlcsSkillsCourse[] = [
  {
    slug: "rights-at-work",
    code: "NSC-101",
    title: "Know Your Rights at Work",
    category: "Workplace & Labour",
    shortDescription:
      "A plain-language tour of the BCEA and LRA — what your employer can and cannot do, leave, overtime, deductions, and unfair labour practices.",
    outcomes: [
      "Read a payslip and spot illegal deductions",
      "Know your leave, overtime and rest-period rights",
      "Recognise unfair labour practices and unfair dismissal",
    ],
    estimatedHours: 4,
    level: "Beginner",
  },
  {
    slug: "ccma-process",
    code: "NSC-102",
    title: "The CCMA Process, Demystified",
    category: "Workplace & Labour",
    shortDescription:
      "How to refer a dispute, prepare for conciliation and arbitration, and what to bring on the day.",
    outcomes: [
      "Complete an LRA Form 7.11 referral",
      "Prepare a bundle and witness list",
      "Understand conciliation vs arbitration",
    ],
    estimatedHours: 5,
    level: "Beginner",
  },
  {
    slug: "disciplinary-hearings",
    code: "NSC-103",
    title: "Disciplinary Hearings — Preparing Your Case",
    category: "Workplace & Labour",
    shortDescription:
      "Procedural fairness, your rights as an employee or representative, and how to cross-examine respectfully.",
    outcomes: [
      "Identify procedural and substantive fairness gaps",
      "Draft an opening statement",
      "Cross-examine without escalating",
    ],
    estimatedHours: 4,
    level: "Intermediate",
  },
  {
    slug: "popia-plain-language",
    code: "NSC-201",
    title: "POPIA in Plain Language (for SMMEs)",
    category: "POPIA & Compliance",
    shortDescription:
      "The eight conditions for lawful processing, what consent really requires, and the smallest POPIA toolkit a small business needs.",
    outcomes: [
      "Map personal information you collect",
      "Write a basic privacy notice and consent clause",
      "Respond to a data-subject request",
    ],
    estimatedHours: 6,
    level: "Beginner",
  },
  {
    slug: "compliance-toolkit-sme",
    code: "NSC-202",
    title: "Basic Compliance Toolkit for Small Business",
    category: "POPIA & Compliance",
    shortDescription:
      "CIPC, SARS, UIF, COIDA, BEE basics — the seven items every owner-managed business should have in a folder.",
    outcomes: [
      "Build a compliance checklist for your business",
      "Understand what an Information Officer does",
      "Know when to escalate to a professional",
    ],
    estimatedHours: 5,
    level: "Beginner",
  },
  {
    slug: "budgeting-money-confidence",
    code: "NSC-301",
    title: "Personal Budgeting & Money Confidence",
    category: "Personal Finance & Debt",
    shortDescription:
      "A zero-based budget you can actually keep, emergency funds, and how to read a bank statement like an auditor.",
    outcomes: [
      "Build a 30-day cash-flow plan",
      "Cut subscription leaks",
      "Set a 3-month emergency-fund goal",
    ],
    estimatedHours: 4,
    level: "Beginner",
  },
  {
    slug: "debt-and-nca",
    code: "NSC-302",
    title: "Dealing with Debt & the National Credit Act",
    category: "Personal Finance & Debt",
    shortDescription:
      "Reckless lending, in duplum, prescription, and what to say (and not say) to a debt collector.",
    outcomes: [
      "Spot reckless credit agreements",
      "Use in duplum and prescription correctly",
      "Reply to a Section 129 letter",
    ],
    estimatedHours: 5,
    level: "Intermediate",
  },
  {
    slug: "debt-review-basics",
    code: "NSC-303",
    title: "Debt Review Basics",
    category: "Personal Finance & Debt",
    shortDescription:
      "When debt review helps, when it hurts, and what the process actually looks like from application to clearance.",
    outcomes: [
      "Decide if debt review is right for you",
      "Understand the role of a debt counsellor",
      "Plan the exit and clearance certificate",
    ],
    estimatedHours: 3,
    level: "Beginner",
  },
  {
    slug: "reading-contracts",
    code: "NSC-401",
    title: "Reading Contracts Like a Pro",
    category: "Contracts & Legal Literacy",
    shortDescription:
      "Indemnities, limitation of liability, breach, cancellation — the clauses non-lawyers always miss.",
    outcomes: [
      "Spot one-sided indemnity clauses",
      "Negotiate cancellation and notice periods",
      "Use a clause-by-clause review checklist",
    ],
    estimatedHours: 5,
    level: "Beginner",
  },
  {
    slug: "leases-and-cpa",
    code: "NSC-402",
    title: "Lease Agreements & Consumer Rights (CPA)",
    category: "Contracts & Legal Literacy",
    shortDescription:
      "Residential leases, deposits, fixed-term cancellations and the Consumer Protection Act protections you can actually rely on.",
    outcomes: [
      "Understand the 20-business-day cancellation rule",
      "Manage deposits and inspections lawfully",
      "Lodge a complaint with the Rental Housing Tribunal",
    ],
    estimatedHours: 4,
    level: "Beginner",
  },
];
